export const SHARED_RESOURCE_SCOPE_VALUES = ["mine", "shared_with_me", "public", "all"] as const;

const SHARED_RESOURCE_SCOPE_QUERY_KEY = "scope";

export type SharedResourceScope = (typeof SHARED_RESOURCE_SCOPE_VALUES)[number];

type SharedResourceCapabilityRecord<TCapability extends string> = {
  owner_user_id?: number | null;
  share_summary?: {
    current_user_capabilities?: TCapability[] | null;
  } | null;
};

function readSharedResourceScopeValue(
  rawScope: string,
  canViewAllScope: boolean,
  defaultScope: SharedResourceScope,
): SharedResourceScope {
  if (!SHARED_RESOURCE_SCOPE_VALUES.includes(rawScope as SharedResourceScope)) {
    return defaultScope;
  }
  if (rawScope === "all" && !canViewAllScope) {
    return defaultScope;
  }
  return rawScope as SharedResourceScope;
}

function readScopeQueryValue() {
  return new URLSearchParams(window.location.search).get(SHARED_RESOURCE_SCOPE_QUERY_KEY) || "";
}

function replaceScopeQueryValue(scope: SharedResourceScope) {
  const query = new URLSearchParams(window.location.search);
  query.set(SHARED_RESOURCE_SCOPE_QUERY_KEY, scope);
  const nextSearch = query.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`;
  window.history.replaceState(null, "", nextUrl);
}

function clearLegacyScopeHashExtraIfPresent() {
  const rawHash = String(window.location.hash || "").replace(/^#/, "");
  if (!rawHash) {
    return false;
  }
  const tokens = rawHash
    .split("&")
    .map((token) => token.trim())
    .filter(Boolean);
  const nextTokens = tokens.filter((token) => !/^scope=/i.test(token));
  if (nextTokens.length === tokens.length) {
    return false;
  }
  const nextHash = nextTokens.length > 0 ? `#${nextTokens.join("&")}` : "";
  const nextUrl = `${window.location.pathname}${window.location.search}${nextHash}`;
  window.history.replaceState(null, "", nextUrl);
  return true;
}

export function readSharedResourceScopeFromQuery(
  canViewAllScope: boolean,
  defaultScope: SharedResourceScope = "mine",
): SharedResourceScope {
  if (typeof window === "undefined") {
    return defaultScope;
  }
  return readSharedResourceScopeValue(readScopeQueryValue().trim(), canViewAllScope, defaultScope);
}

export function replaceSharedResourceScopeQuery(scope: SharedResourceScope) {
  if (typeof window === "undefined") {
    return;
  }
  replaceScopeQueryValue(scope);
  clearLegacyScopeHashExtraIfPresent();
}

export function normalizeSharedResourceScopeQuery(
  canViewAllScope: boolean,
  defaultScope: SharedResourceScope = "mine",
): SharedResourceScope {
  const nextScope = readSharedResourceScopeFromQuery(canViewAllScope, defaultScope);
  if (typeof window === "undefined") {
    return nextScope;
  }
  const rawScope = readScopeQueryValue().trim();
  if (rawScope !== nextScope) {
    replaceSharedResourceScopeQuery(nextScope);
    return nextScope;
  }
  clearLegacyScopeHashExtraIfPresent();
  return nextScope;
}

export function syncNormalizedSharedResourceScopeQuery(
  canViewAllScope: boolean,
  expectedScope?: SharedResourceScope,
): SharedResourceScope {
  if (typeof window === "undefined") {
    return expectedScope ?? "mine";
  }
  const nextScope = readSharedResourceScopeFromQuery(canViewAllScope);
  const rawScope = readScopeQueryValue().trim();
  const normalizedScope = expectedScope ?? nextScope;
  if (rawScope !== normalizedScope) {
    replaceSharedResourceScopeQuery(normalizedScope);
    return normalizedScope;
  }
  if (normalizedScope !== nextScope) {
    replaceSharedResourceScopeQuery(nextScope);
    return nextScope;
  }
  clearLegacyScopeHashExtraIfPresent();
  return normalizedScope;
}

export function buildSharedResourceScopeMenuItems(
  formatScopeLabel: (scope: SharedResourceScope) => string,
  canViewAllScope: boolean,
) {
  const items = [
    { key: "mine", label: formatScopeLabel("mine") },
    { key: "shared_with_me", label: formatScopeLabel("shared_with_me") },
    { key: "public", label: formatScopeLabel("public") },
  ];
  if (canViewAllScope) {
    items.push({ key: "all", label: formatScopeLabel("all") });
  }
  return items;
}

export function hasSharedResourceCapability<TCapability extends string>(
  record: SharedResourceCapabilityRecord<TCapability>,
  capability: TCapability,
): boolean {
  return Boolean(record.share_summary?.current_user_capabilities?.includes(capability));
}

export function isSharedResourceOwner(
  record: Pick<SharedResourceCapabilityRecord<string>, "owner_user_id">,
  actorUserId: number | null,
): boolean {
  return actorUserId !== null && Number(record.owner_user_id) === actorUserId;
}

export function canManageSharedResource(
  record: Pick<SharedResourceCapabilityRecord<string>, "owner_user_id">,
  actorUserId: number | null,
  isAdmin: boolean,
): boolean {
  return isAdmin || isSharedResourceOwner(record, actorUserId);
}
